import {
  AutomatedNotificationType,
  AutomationCreator,
  AutomationParentScope,
  AutomationType,
  getAutomatedNotificationFromConfig,
  IAutomatedNotification,
  IAutomatedNotificationConfiguration,
  IAutomation,
  IAutomationConfiguration,
  IAutomationScopeRequirements,
  IBrand,
  IEvent,
  IRawTemplate,
  isAutomatedNotificationConfiguration,
  ITemplateDefinition,
  ITemplateScopeData,
  SystemTemplate,
  TemplateScope,
  TemplateType,
  TimingDirection,
  TimingUnit,
} from '@principle-theorem/principle-core/interfaces';
import {
  AtLeast,
  DocumentReference,
  Firestore,
  getDoc,
  INamedTypeDocument,
  initFirestoreModel,
  Timestamp,
  WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { DefaultDocumentTemplate } from '../../templating/template-defaults';
import { TemplateDefinition } from '../../templating/template-definition';
import { SYSTEM_TEMPLATES_IDS } from '../../templating/system-templates';
import { Automation } from '../automation/automation';
import { AutomationTiming } from '../automation/automation-timing';

export class AutomatedNotification {
  static init(
    overrides: AtLeast<IAutomatedNotification, 'timing'>
  ): IAutomatedNotification {
    return {
      name: '',
      subject: '',
      type: AutomatedNotificationType.EMAIL,
      ...overrides,
    };
  }

  static getTemplateType(type?: AutomatedNotificationType): TemplateType {
    switch (type) {
      case AutomatedNotificationType.EMAIL:
        return TemplateType.Html;
      default:
        return TemplateType.PlainText;
    }
  }

  static async getTemplateScope(
    notification: IAutomatedNotification,
    configRef?: DocumentReference<IAutomationConfiguration>
  ): Promise<TemplateScope> {
    if (notification.customTemplate) {
      return notification.customTemplate.scope ?? TemplateScope.Appointment;
    }
    if (configRef) {
      const config = await Firestore.getDoc(configRef);
      if (isAutomatedNotificationConfiguration(config)) {
        const parent = await Firestore.getDoc(config.templateRef);
        return parent.scope;
      }
    }
    return TemplateScope.Appointment;
  }

  static async toAutomation(
    notification: IAutomatedNotification,
    creator: INamedTypeDocument<object, AutomationCreator>,
    event: IEvent,
    brandRef: DocumentReference<IBrand>,
    scopeData: IAutomationScopeRequirements,
    configRef?: DocumentReference<IAutomationConfiguration>
  ): Promise<IAutomation<IAutomatedNotification>> {
    return Automation.init({
      type: AutomationType.AutomatedNotification,
      data: notification,
      parentScope: scopeData.parentScope,
      scopeData:
        scopeData.parentScope === AutomationParentScope.Patient
          ? scopeData
          : undefined,
      creator,
      triggerDate: await AutomationTiming.resolveTriggerDateFromEvent(
        event,
        notification.timing
      ),
      brandRef,
      practiceRef: event.practice.ref,
      configRef,
    });
  }

  static async generateFromConfig(
    config: WithRef<IAutomatedNotificationConfiguration>,
    creator: INamedTypeDocument<object, AutomationCreator>,
    event: IEvent,
    brandRef: DocumentReference<IBrand>,
    scopeData: IAutomationScopeRequirements,
    triggerAfterDate?: Timestamp
  ): Promise<IAutomation<IAutomatedNotification>> {
    return Automation.init({
      type: AutomationType.AutomatedNotification,
      data: getAutomatedNotificationFromConfig(config),
      parentScope: scopeData.parentScope,
      scopeData:
        scopeData.parentScope === AutomationParentScope.Patient
          ? scopeData
          : undefined,
      creator,
      triggerDate: await AutomationTiming.resolveTriggerDateFromEvent(
        event,
        config.timing
      ),
      triggerAfterDate,
      brandRef,
      practiceRef: event.practice.ref,
      configRef: config.ref,
    });
  }

  static async resolveRawTemplate<T extends ITemplateScopeData>(
    automation: IAutomation<IAutomatedNotification>,
    scopeData: T,
    preResolvedConfig?: WithRef<IAutomationConfiguration>
  ): Promise<IRawTemplate | undefined> {
    const notification = automation.data;

    if (notification.customTemplate) {
      return {
        type: this.getTemplateType(notification.type),
        scope: notification.customTemplate.scope ?? TemplateScope.None,
        content: notification.customTemplate.content,
        renderedTemplate: notification.customTemplate.renderedTemplate,
      };
    }

    if (automation.configRef) {
      const config = preResolvedConfig ?? (await getDoc(automation.configRef));
      if (!isAutomatedNotificationConfiguration(config)) {
        // eslint-disable-next-line no-console
        console.warn(
          `ConfigRef is not a notification: ${automation.configRef.path}`
        );
        return;
      }
      const template = await getDoc(config.templateRef);
      return TemplateDefinition.resolveRawTemplate(template, scopeData);
    }

    // eslint-disable-next-line no-console
    console.warn(`No resolvable template`);
  }
}

export enum DefaultAutomationConfiguration {
  TwoDaySMSConfirmation = '2-day SMS Confirmation',
}

interface IAutomatedNotificationSeedDefinition {
  templateName?: DefaultDocumentTemplate;
  /**
   * Matched on the deterministic document id rather than the display name, so
   * renaming a system template in settings cannot orphan the seeded config.
   */
  systemTemplate?: SystemTemplate;
  config: AtLeast<
    IAutomatedNotificationConfiguration,
    'name' | 'subject' | 'type' | 'timing'
  >;
}

const notifications: IAutomatedNotificationSeedDefinition[] = [
  {
    templateName: DefaultDocumentTemplate.TwoDaySMSConfirmation,
    config: {
      type: AutomatedNotificationType.SMS,
      name: '2-day SMS Confirmation',
      subject: '',
      timing: {
        amount: 2,
        unit: TimingUnit.Days,
        direction: TimingDirection.Before,
      },
    },
  },
];

function findSeedTemplate(
  item: IAutomatedNotificationSeedDefinition,
  documentTemplates: WithRef<ITemplateDefinition>[]
): WithRef<ITemplateDefinition> | undefined {
  if (item.systemTemplate) {
    const uid = SYSTEM_TEMPLATES_IDS[item.systemTemplate];
    return documentTemplates.find((template) => template.ref.id === uid);
  }
  return documentTemplates.find(
    (template) => template.name === String(item.templateName)
  );
}

export function getDefaultAutomatedNotificationConfigurations(
  documentTemplates: WithRef<ITemplateDefinition>[]
): IAutomatedNotificationConfiguration[] {
  const automations = notifications.map(
    (item): IAutomatedNotificationConfiguration | undefined => {
      const documentTemplate = findSeedTemplate(item, documentTemplates);
      if (!documentTemplate) {
        // eslint-disable-next-line no-console
        console.error(
          `Could not find document template: ${
            item.systemTemplate ?? item.templateName
          }`
        );
        return;
      }
      return {
        ...initFirestoreModel(),
        treatmentRefs: [],
        isActive: true,
        templateRef: documentTemplate.ref,
        ...item.config,
      };
    }
  );

  return compact(automations);
}
